import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'bootstrap'
import { Autocomplete } from '@react-google-maps/api'
import { getUserById, getRoleListForUsers, createUser, updateUser, getBusinessSector, getBusinessType, getInterestSuggestions, getServiceSuggestions } from '../_requests'

const UserAddPage: React.FC = () => {
  const navigate = useNavigate()
  const [userDetails, setuserDetails] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [rolesTypeList, setRolesTypeList] = useState<any[]>([])
  const [resumePreview, setResumePreview] = useState<string | null>(null)
  const [interestInput, setInterestInput] = useState('')
  const [providedServiceInput, setProvidedServiceInput] = useState('')
  const [certInput, setCertInput] = useState('')
  const [businessSector, setBusinessSector] = useState<any>(null)
  const [businessType, setBusinessType] = useState<any>(null)
  const [locations, setLocations] = useState<{ address: string; latitude: number; longitude: number; radiusKm: number | '' }[]>([])
  const locationAutoRef = useRef<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<any>(null)
  const { id } = useParams()
  const [interestSuggestions, setInterestSuggestions] = useState<any[]>([])
  const [providedServiceSuggestions, setProvidedServiceSuggestions] = useState<any[]>([])

  const nurseTypeList = [
    { val: '1', label: 'Registered Nurse (RN)' },
    { val: '2', label: 'Enrolled Nurse (EN)' },
    { val: '3', label: 'Clinical Nurse (CN)' }
  ]

  const fetchRolesTypeList = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getRoleListForUsers()

      if (!response) return

      let roles = response

      // CREATE MODE → only active roles
      if (!id) {
        roles = roles.filter((r: any) => r.is_active === true)
      }

      // UPDATE MODE → active + current user role (even if inactive)
      if (id && userDetails?.user?.user_type) {
        roles = roles.filter(
          (r: any) =>
            r.is_active === true ||
            r.role_slug === userDetails.user.user_type
        )
      }

      const formattedRoles = roles.map((role: any) => ({
        val: role?.role_slug,
        label: role?.role_name,
      }))

      setRolesTypeList(formattedRoles)

    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch roles')
    } finally {
      setLoading(false)
    }
  }, [id, userDetails])

  const fetchBusinessSector = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getBusinessSector()
      setBusinessSector(response?.data || response || [])
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch details')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchBusinessType = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getBusinessType()
      setBusinessType(response?.data || response || [])
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch details')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRolesTypeList()
    fetchBusinessSector();
    fetchBusinessType();
  }, [fetchRolesTypeList, fetchBusinessSector, fetchBusinessType])

  useEffect(() => {
    if (userDetails) {
      fetchRolesTypeList()
    }
  }, [userDetails, fetchRolesTypeList])

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!interestInput || interestInput.length < 2) {
        setInterestSuggestions([])
        return
      }

      try {
        const res = await getInterestSuggestions(interestInput)

        const suggestions =
          res?.data || res?.responseObject || res || []

        setInterestSuggestions(Array.isArray(suggestions) ? suggestions : [])
      } catch (err) {
        console.error('Error fetching interest suggestions:', err)
        setInterestSuggestions([])
      }
    }, 500) // 👉 debounce time (change if needed)

    return () => clearTimeout(handler) // cleanup
  }, [interestInput])

  // Add Tag
  const addCertification = () => {
    const value = certInput.trim()
    if (!value) return

    if (!formData.certifications.includes(value)) {
      setFormData((prev) => ({
        ...prev,
        certifications: [...prev.certifications, value],
      }))
    }

    setCertInput('')
    setError((prev) => ({ ...prev, certifications: '' }))
  }

  // Remove Tag
  const removeCertification = (index: number) => {
    const updated = [...formData.certifications]
    updated.splice(index, 1)

    setFormData((prev) => ({
      ...prev,
      certifications: updated,
    }))
  }

  // Handle Enter
  const handleCertKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addCertification()
    }
  }

  // Suggestions for Interests
  // const handleSearch = async (value: string) => {
  //   if (!value || value.length < 2) {
  //     setInterestSuggestions([])
  //     return
  //   }
  //
  //   try {
  //     const res = await getInterestSuggestions(value)
  //     setInterestSuggestions(res?.data || res || [])
  //   } catch (err) {
  //     console.error('Error fetching interest suggestions:', err)
  //     setInterestSuggestions([])
  //   }
  // }

  // Remove Interest
  const removeInterest = (index: number) => {
    const updated = [...formData.interests]
    updated.splice(index, 1)

    setFormData((prev) => ({
      ...prev,
      interests: updated,
    }))
  }

  // Remove Provided Service
  const removeProvidedService = (index: number) => {
    const updated = [...formData.providedService]
    updated.splice(index, 1)

    setFormData((prev) => ({
      ...prev,
      providedService: updated,
    }))
  }

  // For add multiple location
  const handleAddLocation = () => {
    if (!locationAutoRef.current) return

    const place = locationAutoRef.current.getPlace()

    if (!place?.geometry) return

    const latitude = place.geometry.location.lat()
    const longitude = place.geometry.location.lng()
    const address = place.formatted_address || place.name

    // prevent duplicate
    if (locations.some((l) => l.address === address)) return

    setLocations((prev) => [
      ...prev,
      { address, latitude, longitude, radiusKm: '' }, // default radius
    ])

    // clear input
    const input = document.getElementById('locationInput') as HTMLInputElement
    if (input) input.value = ''
  }

  // Handle radius change
  const handleRadiusChange = (index: number, value: number | '') => {
    if (value !== '' && value < 0) return   // stop negative
    const updated = [...locations]
    updated[index].radiusKm = value
    setLocations(updated)
  }

  // Remove location
  const removeLocation = (index: number) => {
    const updated = [...locations]
    updated.splice(index, 1)
    setLocations(updated)
  }

  const selectProvidedService = (value: string) => {
    if (!formData.providedService.includes(value)) {
      setFormData((prev: any) => ({
        ...prev,
        providedService: [...prev.providedService, value],
      }))
    }

    setProvidedServiceInput('')
    setProvidedServiceSuggestions([])
  }

  useEffect(() => {
    if (id) {
      fetchuserDetails(id)
    }
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    ) as HTMLElement[]

    const instances = tooltipTriggerList.map((el) => new Tooltip(el))

    return () => {
      instances.forEach((i) => i.dispose())
    }
  }, [id])

  // Patch value in users form
  useEffect(() => {
    if (userDetails) {
      setFormData((prev) => ({
        ...prev,
        firstName: userDetails?.user?.first_name,
        lastName: userDetails?.user?.last_name,
        email: userDetails?.user?.email,
        mobile: userDetails?.user?.phone,
        userType: userDetails?.user?.user_type,
        gender: userDetails?.profile_details?.gender || '',
        yearOfBirth: userDetails?.profile_details?.year_of_birth || '',
        relationshipStatus: userDetails?.profile_details?.relationship_status || '',
        ndisNumber: userDetails?.user?.ndis_number || '',
        ndisRegistrationID: userDetails?.user?.ndis_number || '',
        ndisScreeningNumber: userDetails?.user?.ndis_number || '',
        isEmployed: userDetails?.profile_details?.currently_employed || false,
        email_verified: userDetails?.user?.is_email_verified || false,
        mobile_verified: userDetails?.user?.is_mobile_verified || false,
        certifications: userDetails?.certifications?.map((c: any) => c.certification_name) || [],
        interests: userDetails?.user?.interests?.map((interest: any) => interest) || [],
        providedService: userDetails?.user?.services?.map((service: any) => service) || [],

        roleType: userDetails?.profile_details?.role_type || '',
        workingAs: userDetails?.profile_details?.working_as || '',
        isActive: userDetails?.user?.approval_status,
        ndisVerification: userDetails?.user?.ndis_number || '',
        organizationName: userDetails?.profile_details?.organisation_name || '',
        organizationDetails: userDetails?.profile_details?.organisation_details || '',
        serviceAreas: userDetails?.service_areas?.map((serAre: any) => ({ zipcode: serAre.postcode || '', address: `${serAre.suburb}` || `${serAre.sc_service_area}` || '', })) || [],
        businessName: userDetails?.profile_details?.business_name || '',
        ABNNumber: userDetails?.profile_details?.abn_number || '',
        serviceProiderNumber: userDetails?.profile_details?.service_provider_number || '',
        businessSector: userDetails?.profile_details?.business_sector || '',
        businessType: userDetails?.profile_details?.business_type || '',
        facebookURL: userDetails?.profile_details?.facebook_url || '',
        instagramURL: userDetails?.profile_details?.instagram_url || '',
        linkedinURL: userDetails?.profile_details?.linkedin_url || '',
        nurseType: userDetails?.profile_details?.sub_type || '',
      }))

      if (!Array.isArray(userDetails?.locations)) return

      const patchedLocations = userDetails.locations.map((loc: any) => ({
        address: loc.location_name || '',
        latitude: Number(loc.latitude) || 0,
        longitude: Number(loc.longitude) || 0,
        radiusKm:
          loc.service_radius_km === null || loc.service_radius_km === undefined
            ? ''
            : Number(loc.service_radius_km),
      }))

      setLocations(patchedLocations)

      if (userDetails?.user?.profile_picture_url) {
        setImagePreview(userDetails?.user?.profile_picture_url)
      }
      if (userDetails?.profile_details?.resume_file_url) {
        setResumePreview(userDetails.profile_details.resume_file_url)
      }
    }
  }, [userDetails])

  const fetchuserDetails = async (eventId: string) => {
    try {
      setLoading(true)
      const response = await getUserById(eventId as any)
      setuserDetails(response || {})
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch event details'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    userType: '',
    password: '',
    gender: '',
    yearOfBirth: '',
    relationshipStatus: '',
    ndisNumber: '',
    ndisRegistrationID: '',
    ndisScreeningNumber: '',
    resume: '' as File | '',
    isEmployed: false,
    email_verified: false,
    mobile_verified: false,
    interests: [] as string[],
    certifications: [] as string[],
    roleType: '',
    workingAs: '',
    isActive: '',
    ndisVerification: '',
    organizationName: '',
    organizationDetails: '',
    serviceAreas: [] as { zipcode: string; address: string }[],
    businessName: '',
    nurseType: '',
    ABNNumber: '',
    serviceProiderNumber: '',
    businessSector: '',
    businessType: '',
    facebookURL: '',
    instagramURL: '',
    linkedinURL: '',
    providedService: [] as string[],
  })

  const [error, setError] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    userType: '',
    password: '',
    gender: '',
    yearOfBirth: '',
    relationshipStatus: '',
    ndisNumber: '',
    ndisRegistrationID: '',
    ndisScreeningNumber: '',
    resume: '',
    interests: '',
    certifications: '',
    roleType: '',
    workingAs: '',
    isActive: '',
    ndisVerification: '',
    organizationName: '',
    organizationDetails: '',
    serviceAreas: '',
    businessName: '',
    nurseType: '',
    ABNNumber: '',
    serviceProiderNumber: '',
    businessSector: '',
    businessType: '',
    facebookURL: '',
    instagramURL: '',
    linkedinURL: '',
    providedService: '',
  })

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{10}$/;
    const yearOfBirthRegex = /^[0-9]{4}$/;
    const currentYear = new Date().getFullYear();
    const ndisRegex = /^[0-9]{8,12}$/;
    const abnRegex = /^[0-9]{8,11}$/;
    const serviceProviderRegex = /^[0-9]{8,10}$/;
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
    const linkRegex = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
    const newError = {
      firstName: formData.firstName ? '' : 'First name is required',
      lastName: formData.lastName ? '' : 'Last name is required',
      email: !formData.email ? 'Email is required' : !emailRegex.test(formData.email) ? 'Please enter a valid email' : '',
      mobile: !formData.mobile ? 'Mobile number is required' : !mobileRegex.test(formData.mobile) ? 'Enter valid 10 digit mobile number' : '',
      userType: formData.userType ? '' : 'User type is required',
      isActive: formData.isActive ? '' : 'Status is required',
      password: id ? '' : !formData.password ? 'Password is required' : !passwordRegex.test(formData.password) ? 'Password must be at least 6 characters with 1 uppercase, 1 number & 1 special character' : '',
      // Conditional validation based on user type
      gender: (formData.userType === 'participant' || formData.userType === 'support_coordinator_lac') && !formData.gender ? 'Gender is required' : '',
      yearOfBirth:
        (formData.userType === 'participant' || formData.userType === 'support_coordinator_lac') &&
          !formData.yearOfBirth
          ? 'Birth year is required'
          : formData.yearOfBirth && !yearOfBirthRegex.test(formData.yearOfBirth)
            ? 'Enter valid 4 digit birth year'
            : formData.yearOfBirth && Number(formData.yearOfBirth) > currentYear
              ? `Birth year cannot be greater than ${currentYear}`
              : '',
      relationshipStatus: formData.userType === 'participant' && !formData.relationshipStatus ? 'Relationship status is required' : '',
      ndisNumber: (formData.userType === 'participant') && !formData.ndisNumber ? 'NDIS number is required' : formData.ndisNumber && !ndisRegex.test(formData.ndisNumber) ? 'NDIS number must be 8 to 12 digits' : '',
      ndisRegistrationID: (formData.userType === 'ndis_business' || formData.userType === 'service_provider' || formData.userType === 'allied_health') && !formData.ndisRegistrationID ? 'NDIS registration id is required' : formData.ndisRegistrationID && !ndisRegex.test(formData.ndisRegistrationID) ? 'NDIS registration id must be 8 to 12 digits' : '',
      ndisScreeningNumber: (formData.userType === 'support_worker' || formData.userType === 'nurse') && !formData.ndisScreeningNumber ? 'NDIS screening number is required' : formData.ndisScreeningNumber && !ndisRegex.test(formData.ndisScreeningNumber) ? 'NDIS screening number must be 8 to 12 digits' : '',
      resume: (formData.userType === 'support_worker' || formData.userType === 'nurse') && !formData.resume && !resumePreview ? 'Resume is required' : '',
      certifications: (formData.userType === 'support_worker' || formData.userType === 'nurse') && formData.certifications.length === 0 ? 'At least one certification is required' : '',
      interests: (formData.userType === 'participant' || formData.userType === 'support_worker' || formData.userType === 'nurse' || formData.userType === 'support_coordinator_lac') && formData.interests.length === 0 ? 'At least one interest is required' : '',
      roleType: formData.userType === 'support_coordinator_lac' && !formData.roleType ? 'Role type is required' : '',
      workingAs: formData.userType === 'support_coordinator_lac' && !formData.roleType ? 'Working as is required' : '',
      ndisVerification: (formData.userType === 'support_coordinator_lac') && !formData.ndisVerification ? 'NDIS verification number is required' : formData.ndisVerification && !ndisRegex.test(formData.ndisVerification) ? 'NDIS screening number must be 8 to 12 digits' : '',
      organizationName: formData.userType === 'support_coordinator_lac' && !formData.organizationName ? 'Organization name is required' : '',
      organizationDetails: (formData.userType === 'support_coordinator_lac' && formData.workingAs === 'organisation') && !formData.organizationDetails ? 'Organization details is required' : '',
      serviceAreas: formData.userType === 'support_coordinator_lac' && formData.serviceAreas.length === 0 ? 'At least one service area is required' : '',
      businessName: (formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && !formData.businessName ? 'Business name is required' : '',
      nurseType: formData.userType === 'nurse' && !formData.nurseType ? 'Nurse type is required' : '',
      ABNNumber: (formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && !formData.ABNNumber ? 'ABN number is required' : formData.ABNNumber && !abnRegex.test(formData.ABNNumber) ? 'ABN number must be 8 to 11 digits' : '',
      serviceProiderNumber: (formData.userType === 'service_provider' || formData.userType === 'ndis_business') && !formData.serviceProiderNumber ? 'Service provider number is required' : formData.serviceProiderNumber && !serviceProviderRegex.test(formData.serviceProiderNumber) ? 'Service provider number must be 8 to 10 digits' : '',
      businessSector: (formData.userType === 'allied_health' || formData.userType === 'service_provider') && !formData.businessSector ? 'Business sector is required' : '',
      businessType: formData.userType === 'ndis_business' && !formData.businessType ? 'Business type is required' : '',
      facebookURL: (formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && formData.facebookURL && !linkRegex.test(formData.facebookURL) ? 'Enter valid Facebook URL' : '',
      instagramURL: (formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && formData.instagramURL && !linkRegex.test(formData.instagramURL) ? 'Enter valid instagram URL' : '',
      linkedinURL: (formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && formData.linkedinURL && !linkRegex.test(formData.linkedinURL) ? 'Enter valid linkedin URL' : '',
      providedService: (formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && formData.providedService.length === 0 ? 'At least one service is required' : '',
    }

    setError(newError)

    return Object.values(newError).every((err) => err === '')
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {

    const { name, value } = e.target

    let updatedValue = value

    if (name === 'userType') {
      setFormData((prev) => ({
        ...prev,
        userType: value,

        // reset all type-based fields
        gender: '',
        yearOfBirth: '',
        relationshipStatus: '',
        ndisNumber: '',
        ndisRegistrationID: '',
        ndisScreeningNumber: '',
        nurseType: '',
        roleType: '',
        workingAs: '',
        organizationName: '',
        organizationDetails: '',
        businessName: '',
        ABNNumber: '',
        serviceProiderNumber: '',
        businessSector: '',
        businessType: '',
        interests: [],
        certifications: [],
        providedService: [],
        serviceAreas: [],
        facebookURL: '',
        instagramURL: '',
        linkedinURL: '',
      }))

      setLocations([])

      return
    }

    // Only digits
    if (name === 'mobile' || name === 'yearOfBirth' || name === 'ndisNumber' || name === 'ndisScreeningNumber' || name === 'ndisVerification' || name === 'ndisRegistrationID') {
      updatedValue = value.replace(/\D/g, '')
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }))

    setError((prev) => ({
      ...prev,
      [name]: '',
    }))
  }

  const selectInterest = (value: string) => {
    if (!formData.interests.includes(value)) {
      setFormData((prev: any) => ({
        ...prev,
        interests: [...prev.interests, value],
      }))
    }

    setInterestInput("")
    setInterestSuggestions([])
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // ✅ Allowed types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']

    if (!allowedTypes.includes(file.type)) {
      toast.error('Only .jpg, .jpeg, .png files are allowed')

      // reset input + states
      e.target.value = ''
      setImageFile(null)
      setImagePreview(null)
      return
    }

    // ✅ valid file
    setImageFile(file)

    const reader = new FileReader()
    reader.onload = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const buildPayloadByUserType = () => {

    //COMMON PAYLOAD
    const commonPayload: any = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.mobile,
      approval_status: formData.isActive,
      is_email_verified: formData.email_verified ? 1 : 0,
      is_mobile_verified: formData.mobile_verified ? 1 : 0,
    }

    if (!id) {
      commonPayload.user_type = formData.userType || ''
    }

    if (formData.password?.trim()) {
      commonPayload.password = formData.password
    }

    let typePayload: any = {}

    switch (formData.userType) {

      // ---------------- PARTICIPANT ----------------
      case 'participant':
        typePayload = {
          gender: formData.gender,
          year_of_birth: formData.yearOfBirth,
          relationship_status: formData.relationshipStatus,
          ndis_number: formData.ndisNumber,
          interests: formData.interests || [],
        }
        break


      // ---------------- SUPPORT WORKER ----------------
      case 'support_worker':

        typePayload = {
          ndis_number: formData.ndisScreeningNumber,
          interests: formData.interests || [],
          certifications: (formData.certifications || []).map((c) => ({
            name: c,
            source: "manual"
          })),
          currently_employed: formData.isEmployed ? 1 : 0,
          locations: locations.map((loc) => ({
            location_name: loc.address,
            latitude: loc.latitude,
            longitude: loc.longitude,
            service_radius_km: loc.radiusKm === '' ? null : loc.radiusKm,
          }))
        }
        break

      // ---------------- NURSE ----------------
      case 'nurse':

        typePayload = {
          ndis_number: formData.ndisScreeningNumber,
          interests: formData.interests || [],
          certifications: (formData.certifications || []).map((c) => ({
            name: c,
            source: "manual"
          })),
          currently_employed: formData.isEmployed ? 1 : 0,
          sub_type: formData.nurseType,
          locations: locations.map((loc) => ({
            location_name: loc.address,
            latitude: loc.latitude,
            longitude: loc.longitude,
            service_radius_km: loc.radiusKm === '' ? null : loc.radiusKm,
          }))
        }

        break


      // ---------------- SUPPORT COORDINATOR ----------------
      case 'support_coordinator_lac':

        typePayload = {
          gender: formData.gender,
          year_of_birth: formData.yearOfBirth,
          role_type: formData.roleType,
          working_as: formData.workingAs,
          interests: formData.interests || [],
          ndis_number: formData.ndisVerification,
          organisation_name: formData.organizationName,
          organisation_details: formData.organizationDetails,
          service_areas: (formData.serviceAreas || []).map((area) => {
            const suburb = area.address.split(',')[0] || ''
            return {
              postcode: area.zipcode,
              scServiceArea: `${area.zipcode} (${suburb})`,
              suburb: suburb,
            }
          })
        }

        break


      // ---------------- ALLIED HEALTH ----------------
      case 'allied_health':

        typePayload = {
          business_name: formData.businessName,
          ndis_number: formData.ndisRegistrationID,
          abn_number: formData.ABNNumber,
          business_sector: formData.businessSector,
          facebook_url: formData.facebookURL,
          instagram_url: formData.instagramURL,
          linkedin_url: formData.linkedinURL,
          services: formData.providedService || []
        }

        break


      // ---------------- SERVICE PROVIDER ----------------
      case 'service_provider':

        typePayload = {
          business_name: formData.businessName,
          ndis_number: formData.ndisRegistrationID,
          abn_number: formData.ABNNumber,
          service_provider_number: formData.serviceProiderNumber,
          business_sector: formData.businessSector,
          facebook_url: formData.facebookURL,
          instagram_url: formData.instagramURL,
          linkedin_url: formData.linkedinURL,
          locations: locations.map((loc) => ({
            location_name: loc.address,
            latitude: loc.latitude,
            longitude: loc.longitude,
            service_radius_km: loc.radiusKm === '' ? null : loc.radiusKm,
          })),
          services: formData.providedService || [],
        }

        break


      // ---------------- NDIS BUSINESS ----------------
      case 'ndis_business':

        typePayload = {
          business_name: formData.businessName,
          abn_number: formData.ABNNumber,
          service_provider_number: formData.serviceProiderNumber,
          business_sector: formData.businessSector,
          business_type: formData.businessType,
          ndis_number: formData.ndisRegistrationID,
          facebook_url: formData.facebookURL,
          instagram_url: formData.instagramURL,
          linkedin_url: formData.linkedinURL,
          locations: locations.map((loc) => ({
            location_name: loc.address,
            latitude: loc.latitude,
            longitude: loc.longitude,
            service_radius_km: loc.radiusKm === '' ? null : loc.radiusKm,
          })),
          services: formData.providedService || [],
        }

        break

      default:
        break
    }

    return { ...commonPayload, ...typePayload }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setLoading(true)

    try {
      const payload = buildPayloadByUserType()
      console.log("FINAL PAYLOAD:", payload)
      const fd = new FormData()

      // append dynamic payload
      Object.keys(payload).forEach((key) => {
        const value = payload[key]

        if (Array.isArray(value) || typeof value === 'object') {
          fd.append(key, JSON.stringify(value))
        } else {
          fd.append(key, value ?? '')
        }
      })

      // files
      if (imageFile) fd.append('profile_picture', imageFile)
      if (formData.resume) fd.append('resume', formData.resume as File)

      let resp: any

      if (id) {
        resp = await updateUser(fd as any, id as any)
        toast.success(resp?.message)
      } else {
        resp = await createUser(fd as any)
        toast.success(resp?.message)
      }
      navigate('/users')

    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save user')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className='card'>
        <div className='card-body'>
          <form onSubmit={handleSubmit} noValidate>
            <div className='row mb-7'>
              {/* Profile Image */}
              <div className='col-md-12 mb-7'>
                <label className='form-label fw-bold text-black mb-3'>Profile Image</label>
                <div className='d-flex align-items-center'>
                  <div className='me-4 symbol symbol-125px'>
                    <img
                      src={imagePreview || '/media/avatars/placeholder.png'}
                      alt='Profile'
                      className='navbar-user-icon img-theme-border'
                    />
                  </div>
                  <div>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary'
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Change Image
                    </button>
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='.jpg,.jpeg,.png,.PNG'
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>

              {/* First Name */}
              <div className='col-md-6 mb-7'>
                <label htmlFor='firstName' className='required form-label fw-bold text-black'>First Name</label>
                <input
                  type='text'
                  id='firstName'
                  className={`form-control ${error.firstName ? 'is-invalid' : ''}`}
                  name='firstName'
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder='Enter first name'
                  autoComplete='off'
                  required
                />
                {error.firstName && <div className='invalid-feedback'>{error.firstName}</div>}
              </div>

              {/* Last Name */}
              <div className='col-md-6 mb-7'>
                <label htmlFor='lastName' className='required form-label fw-bold text-black'>Last Name</label>
                <input
                  type='text'
                  id='lastName'
                  className={`form-control ${error.lastName ? 'is-invalid' : ''}`}
                  name='lastName'
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder='Enter last name'
                  autoComplete='off'
                  required
                />
                {error.lastName && <div className='invalid-feedback'>{error.lastName}</div>}
              </div>

              {/* email */}
              <div className='col-md-6 mb-7'>
                <label htmlFor='email' className='required form-label fw-bold text-black'>Email</label>
                <input
                  type='text'
                  id='email'
                  className={`form-control ${error.email ? 'is-invalid' : ''}`}
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  placeholder='Enter email'
                  autoComplete='off'
                  required
                />
                {error.email && <div className='invalid-feedback'>{error.email}</div>}
              </div>

              {/* mobile */}
              <div className='col-md-6 mb-7'>
                <label htmlFor='mobile' className='required form-label fw-bold text-black'>
                  Mobile Number
                </label>
                <input
                  type='text'
                  id='mobile'
                  className={`form-control ${error.mobile ? 'is-invalid' : ''}`}
                  name='mobile'
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder='Enter mobile number'
                  maxLength={10}
                  autoComplete='off'
                  required
                />
                {error.mobile && <div className='invalid-feedback'>{error.mobile}</div>}
              </div>

              {/* Password */}
              <div className='col-md-6 mb-7 password-field'>
                <label className='form-label fs-6 fw-bolder text-dark'>
                  Password <span className='text-danger'>*</span>
                </label>

                <div className='input-group'>
                  <input
                    name='password'
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control ${error.password ? 'is-invalid' : ''}`}
                    placeholder='Enter password'
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete='off'
                  />
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={`bi bi-${showPassword ? 'eye' : 'eye-slash'}`}></i>
                  </button>
                </div>
                {error.password && (
                  <div className='invalid-feedback d-block'>{error.password}</div>
                )}
              </div>

              <div className='col-md-6 mb-7'>
                <label className='form-label fw-bold text-black d-block mb-6'>
                  Email Verification
                </label>

                <div className='d-flex align-items-center justify-content-between'>
                  <p className='mb-0 fw-semibold'>Email Verified?</p>

                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={formData.email_verified}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          email_verified: e.target.checked,
                        }))
                      }
                    />
                    <label className='form-check-label'>
                      {formData.email_verified ? 'Yes' : 'No'}
                    </label>
                  </div>
                </div>
              </div>

              <div className='col-md-6 mb-7'>
                <label className='form-label fw-bold text-black d-block mb-6'>
                  Mobile Verification
                </label>

                <div className='d-flex align-items-center justify-content-between'>
                  <p className='mb-0 fw-semibold'>Mobile Verified?</p>

                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={formData.mobile_verified}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          mobile_verified: e.target.checked,
                        }))
                      }
                    />
                    <label className='form-check-label'>
                      {formData.mobile_verified ? 'Yes' : 'No'}
                    </label>
                  </div>
                </div>
              </div>

              <div className='col-md-6 mb-7'>
                <label htmlFor='isActive' className='required form-label fw-bold text-black'>
                  Status
                </label>

                <select
                  id='isActive'
                  name='isActive'
                  value={formData.isActive}
                  onChange={handleChange}
                  className={`form-select ${error.isActive ? 'is-invalid' : ''}`}
                >
                  <option value=''>Select status</option>
                  <option value='pending'>Pending</option>
                  <option value='approved'>Approve</option>
                  <option value='rejected'>Reject</option>
                </select>

                {error.isActive && <div className='invalid-feedback'>{error.isActive}</div>}
              </div>

              {/* User Type */}
              <div className='col-md-6 mb-7'>
                <label htmlFor='userType' className='required form-label fw-bold text-black'>
                  User Type
                </label>

                {/* <select
                  id='userType'
                  name='userType'
                  value={formData.userType}
                  onChange={handleChange}
                  className={`form-select ${error.userType ? 'is-invalid' : ''}`}
                  disabled={!!id}   //readonly when id exists
                  required
                >
                  <option value=''>Select user type</option>

                  {rolesTypeList.map((role) => (
                    <option key={role.val} value={role.val}>
                      {role.label}
                    </option>
                  ))}
                </select> */}
                <select
                  id='userType'
                  name='userType'
                  value={formData.userType}
                  onChange={handleChange}
                  className={`form-select ${error.userType ? 'is-invalid' : ''}`}
                  disabled={!!id}
                  required
                >
                  <option value=''>Select user type</option>

                  {rolesTypeList.map((role) => (
                    <option key={role.val} value={role.val}>
                      {role.label}
                    </option>
                  ))}
                </select>

                {error.userType && <div className='invalid-feedback'>{error.userType}</div>}
              </div>

              {/* Nurse Type */}
              {formData.userType === 'nurse' && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='nurseType' className='required form-label fw-bold text-black'>
                    Nurse Type
                  </label>

                  <select
                    id='nurseType'
                    name='nurseType'
                    value={formData.nurseType}
                    onChange={handleChange}
                    className={`form-select ${error.nurseType ? 'is-invalid' : ''}`}
                    required
                  >
                    <option value=''>Select nurse type</option>

                    {nurseTypeList.map((nurse) => (
                      <option key={nurse.label} value={nurse.label}>
                        {nurse.label}
                      </option>
                    ))}
                  </select>

                  {error.nurseType && <div className='invalid-feedback'>{error.nurseType}</div>}
                </div>
              )}

              {/* Gender (Only for Participant) */}
              {(formData.userType === 'participant' || formData.userType === 'support_coordinator_lac') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='gender' className='required form-label fw-bold text-black'>
                    Gender
                  </label>

                  <select
                    id='gender'
                    name='gender'
                    value={formData.gender}
                    onChange={handleChange}
                    className={`form-select ${error.gender ? 'is-invalid' : ''}`}
                  >
                    <option value=''>Select gender</option>
                    <option value='male'>Male</option>
                    <option value='female'>Female</option>
                    <option value='other'>Other</option>
                  </select>

                  {error.gender && <div className='invalid-feedback'>{error.gender}</div>}
                </div>
              )}

              {/* Birth Year (Only for Participant) */}
              {(formData.userType === 'participant' || formData.userType === 'support_coordinator_lac') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='yearOfBirth' className='required form-label fw-bold text-black'>
                    Birth Year
                  </label>
                  <input
                    type='text'
                    id='yearOfBirth'
                    className={`form-control ${error.yearOfBirth ? 'is-invalid' : ''}`}
                    name='yearOfBirth'
                    value={formData.yearOfBirth}
                    onChange={handleChange}
                    placeholder='Enter birth year'
                    maxLength={4}
                    autoComplete='off'
                    required
                  />
                  {error.yearOfBirth && <div className='invalid-feedback'>{error.yearOfBirth}</div>}
                </div>
              )}

              {/* Relationship Status (Only for Participant) */}
              {formData.userType === 'participant' && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='relationshipStatus' className='required form-label fw-bold text-black'>
                    Relationship Status
                  </label>

                  <select
                    id='relationshipStatus'
                    name='relationshipStatus'
                    value={formData.relationshipStatus}
                    onChange={handleChange}
                    className={`form-select ${error.relationshipStatus ? 'is-invalid' : ''}`}
                  >
                    <option value=''>Select relationship status</option>
                    <option value='single'>Single</option>
                    <option value='married'>Married</option>
                    <option value='prefer_not_to_say'>Prefer not to say</option>
                  </select>

                  {error.relationshipStatus && <div className='invalid-feedback'>{error.relationshipStatus}</div>}
                </div>
              )}

              {/* NDIS number (Only for Participant) */}
              {formData.userType === 'participant' && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='ndisNumber' className='required form-label fw-bold text-black'>
                    NDIS Verification
                  </label>
                  <input
                    type='text'
                    id='ndisNumber'
                    className={`form-control ${error.ndisNumber ? 'is-invalid' : ''}`}
                    name='ndisNumber'
                    value={formData.ndisNumber}
                    onChange={handleChange}
                    placeholder='Enter NDIS verification'
                    maxLength={12}
                    autoComplete='off'
                    required
                  />
                  {error.ndisNumber && <div className='invalid-feedback'>{error.ndisNumber}</div>}
                </div>
              )}

              {/* NDIS number (Only for Participant) */}
              {(formData.userType === 'ndis_business' || formData.userType === 'service_provider' || formData.userType === 'allied_health') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='ndisRegistrationID' className='required form-label fw-bold text-black'>
                    NDIS Registration ID
                  </label>
                  <input
                    type='text'
                    id='ndisRegistrationID'
                    className={`form-control ${error.ndisRegistrationID ? 'is-invalid' : ''}`}
                    name='ndisRegistrationID'
                    value={formData.ndisRegistrationID}
                    onChange={handleChange}
                    placeholder='Enter NDIS verification'
                    maxLength={12}
                    autoComplete='off'
                    required
                  />
                  {error.ndisRegistrationID && <div className='invalid-feedback'>{error.ndisRegistrationID}</div>}
                </div>
              )}

              {/* NDIS number (Only for support_worker) */}
              {(formData.userType === 'support_worker' || formData.userType === 'nurse') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='ndisScreeningNumber' className='required form-label fw-bold text-black'>
                    NDIS Screening Check
                  </label>
                  <input
                    type='text'
                    id='ndisScreeningNumber'
                    className={`form-control ${error.ndisScreeningNumber ? 'is-invalid' : ''}`}
                    name='ndisScreeningNumber'
                    value={formData.ndisScreeningNumber}
                    onChange={handleChange}
                    placeholder='Enter NDIS screening check '
                    maxLength={12}
                    autoComplete='off'
                    required
                  />
                  {error.ndisScreeningNumber && <div className='invalid-feedback'>{error.ndisScreeningNumber}</div>}
                </div>
              )}

              {/* Resume (Only for support_worker) */}
              {(formData.userType === 'support_worker' || formData.userType === 'nurse') && (
                <div className='col-md-6 mb-7'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <label htmlFor='resume' className='required form-label fw-bold text-black me-3'>
                      Resume
                    </label>

                    {/* Show existing resume */}
                    {resumePreview && !formData.resume && (
                      <div className='mb-2'>
                        <a href={resumePreview} target='_blank' rel='noopener noreferrer'>
                          View Uploaded Resume
                        </a>
                      </div>
                    )}
                  </div>

                  <input
                    type='file'
                    id='resume'
                    name='resume'
                    accept='.pdf,.doc,.docx'
                    className={`form-control ${error.resume ? 'is-invalid' : ''}`}
                    onChange={(e) => {
                      const file = e.target.files?.[0]

                      setFormData((prev) => ({
                        ...prev,
                        resume: file || '',
                      }))

                      setResumePreview(null) // ✅ hide old when new selected

                      setError((prev) => ({
                        ...prev,
                        resume: '',
                      }))
                    }}
                  />

                  {error.resume && <div className='invalid-feedback'>{error.resume}</div>}
                </div>
              )}

              {/* Interests Tag Input */}
              {(formData.userType === 'participant' || formData.userType === 'support_worker' || formData.userType === 'nurse' || formData.userType === 'support_coordinator_lac') && (
                <div className='col-md-6 mb-7 position-relative'>
                  <label className='required form-label fw-bold text-black'>
                    Interests
                  </label>

                  {/* Tag List */}
                  <div className='d-flex flex-wrap gap-2'>
                    {formData.interests.map((tag, index) => (
                      <span key={index} className='badge bg-success d-flex align-items-center text-white mb-2'>
                        {tag}
                        <button
                          type='button'
                          className='btn-close btn-close-white ms-2'
                          style={{ fontSize: '10px' }}
                          onClick={() => removeInterest(index)}
                        />
                      </span>
                    ))}
                  </div>

                  {/* Input */}
                  <input
                    type="text"
                    className={`form-control ${error.interests ? 'is-invalid' : ''}`}
                    placeholder="Search & select interest"
                    value={interestInput}
                    onChange={async (e) => {
                      const value = e.target.value
                      setInterestInput(value)

                      if (value.length < 2) {
                        setInterestSuggestions([])
                        return
                      }

                      try {
                        const res = await getInterestSuggestions(value)

                        const suggestions = res?.data || res?.responseObject || res || []

                        setInterestSuggestions(Array.isArray(suggestions) ? suggestions : [])
                      } catch (err) {
                        console.error("Error fetching interest suggestions:", err)
                        setInterestSuggestions([])
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                      }
                    }}
                  />

                  {interestSuggestions.length > 0 && (
                    <div className="border rounded bg-white mt-1 position-absolute w-100 z-index-10 shadow-sm" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {interestSuggestions.map((item: any, i: number) => (
                        <div
                          key={i}
                          className="p-2 cursor-pointer hover:bg-gray-50"
                          style={{ cursor: "pointer", color: "black" }}
                          onClick={() => selectInterest(item)}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  )}

                  {error.interests && (
                    <div className='invalid-feedback d-block'>{error.interests}</div>
                  )}
                </div>
              )}

              {/* Certifications Tag Input */}
              {(formData.userType === 'support_worker' || formData.userType === 'nurse') && (
                <div className='col-md-6 mb-7'>
                  <label className='required form-label fw-bold text-black'>
                    Certifications
                  </label>

                  {/* Tag List */}
                  <div className='d-flex flex-wrap gap-2'>
                    {formData.certifications.map((tag, index) => (
                      <span key={index} className='badge bg-success d-flex align-items-center text-white mb-2'>
                        {tag}
                        <button
                          type='button'
                          className='btn-close btn-close-white ms-2'
                          style={{ fontSize: '10px' }}
                          onClick={() => removeCertification(index)}
                        />
                      </span>
                    ))}
                  </div>

                  {/* Input */}
                  <input
                    type='text'
                    className={`form-control ${error.certifications ? 'is-invalid' : ''}`}
                    placeholder='Type certification and press Enter'
                    value={certInput}
                    onChange={(e) => setCertInput(e.target.value)}
                    onKeyDown={handleCertKeyDown}
                  />

                  {error.certifications && (
                    <div className='invalid-feedback d-block'>{error.certifications}</div>
                  )}
                </div>
              )}

              {/* isEmployed Toggle (Only for support_worker) */}
              {(formData.userType === 'support_worker' || formData.userType === 'nurse') && (
                <div className='col-md-6 mb-7'>
                  <label className='form-label fw-bold text-black d-block mb-6'>
                    Work Status
                  </label>

                  <div className='d-flex align-items-center justify-content-between'>
                    <p className='font-14 text-theme-gray mb-0 fw-semibold'>Currently Employed?</p>
                    <div className='form-check form-switch'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='isEmployed'
                        checked={formData.isEmployed}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isEmployed: e.target.checked,
                          }))
                        }
                      />
                      <label className='form-check-label' htmlFor='isEmployed'>
                        {formData.isEmployed ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* roleType (Only for support_coordinator_lac) */}
              {formData.userType === 'support_coordinator_lac' && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='roleType' className='required form-label fw-bold text-black'>
                    Role Type
                  </label>

                  <select
                    id='roleType'
                    name='roleType'
                    value={formData.roleType}
                    onChange={handleChange}
                    className={`form-select ${error.roleType ? 'is-invalid' : ''}`}
                  >
                    <option value=''>Select role type</option>
                    <option value='support_coordinator'>Support Coordinator</option>
                    <option value='lac'>LAC</option>
                  </select>

                  {error.roleType && <div className='invalid-feedback'>{error.roleType}</div>}
                </div>
              )}

              {/* workingAs (Only for workingAs) */}
              {formData.userType === 'support_coordinator_lac' && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='workingAs' className='required form-label fw-bold text-black'>
                    Working As
                  </label>

                  <select
                    id='workingAs'
                    name='workingAs'
                    value={formData.workingAs}
                    onChange={handleChange}
                    className={`form-select ${error.workingAs ? 'is-invalid' : ''}`}
                  >
                    <option value=''>Select working as</option>
                    {formData.roleType === 'support_coordinator' && (
                      <>
                        <option value='individual'>Individual</option>
                        <option value='organisation'>Organisation</option>
                      </>
                    )}
                    {formData.roleType === 'lac' && (
                      <>
                        <option value='organisation'>Organisation</option>
                      </>
                    )}
                  </select>

                  {error.workingAs && <div className='invalid-feedback'>{error.workingAs}</div>}
                </div>
              )}

              {/* NDIS verification (Only for support_coordinator_lac) */}
              {formData.userType === 'support_coordinator_lac' && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='ndisVerification' className='required form-label fw-bold text-black'>
                    NDIS Verification
                  </label>
                  <input
                    type='text'
                    id='ndisVerification'
                    className={`form-control ${error.ndisVerification ? 'is-invalid' : ''}`}
                    name='ndisVerification'
                    value={formData.ndisVerification}
                    onChange={handleChange}
                    placeholder='Enter NDIS verification number'
                    maxLength={12}
                    autoComplete='off'
                    required
                  />
                  {error.ndisVerification && <div className='invalid-feedback'>{error.ndisVerification}</div>}
                </div>
              )}

              {/* Organization name (Only for support_coordinator_lac) */}
              {(formData.userType === 'support_coordinator_lac' && formData.workingAs === 'organisation') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='organizationName' className='required form-label fw-bold text-black'>Organization Name</label>
                  <input
                    type='text'
                    id='organizationName'
                    className={`form-control ${error.organizationName ? 'is-invalid' : ''}`}
                    name='organizationName'
                    value={formData.organizationName}
                    onChange={handleChange}
                    placeholder='Enter organization name'
                    autoComplete='off'
                    required
                  />
                  {error.organizationName && <div className='invalid-feedback'>{error.organizationName}</div>}
                </div>
              )}

              {/* Organization details (Only for support_coordinator_lac) */}
              {(formData.userType === 'support_coordinator_lac' && formData.workingAs === 'organisation') && (
                <div className='col-md-6 mb-7'>
                  <label
                    htmlFor='organizationDetails'
                    className='required form-label fw-bold text-black'
                  >
                    Organization Details
                  </label>

                  <textarea
                    id='organizationDetails'
                    name='organizationDetails'
                    className={`form-control ${error.organizationDetails ? 'is-invalid' : ''}`}
                    value={formData.organizationDetails}
                    onChange={handleChange}
                    placeholder='Enter organization details'
                    autoComplete='off'
                    rows={3}
                  />

                  {error.organizationDetails && (
                    <div className='invalid-feedback'>{error.organizationDetails}</div>
                  )}
                </div>
              )}

              {/* Service Areas (Multiple Zipcode Autocomplete) */}
              {formData.userType === 'support_coordinator_lac' && (
                <div className='col-md-6 mb-7'>
                  <label className='required form-label fw-bold text-black'>
                    Service Areas
                  </label>

                  {/* Selected Tags */}
                  <div className='d-flex flex-wrap gap-2'>
                    {formData.serviceAreas.map((item, index) => (
                      <span
                        key={index}
                        className='badge bg-success d-flex align-items-center text-white mb-2'
                        title={item.address}
                      >
                        {item.zipcode} - {item.address}

                        <button
                          type='button'
                          className='btn-close btn-close-white ms-2'
                          style={{ fontSize: '10px' }}
                          onClick={() => {
                            const updated = [...formData.serviceAreas]
                            updated.splice(index, 1)

                            setFormData((prev) => ({
                              ...prev,
                              serviceAreas: updated,
                            }))
                          }}
                        />
                      </span>
                    ))}
                  </div>

                  <Autocomplete
                    onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                    onPlaceChanged={() => {
                      if (!autocompleteRef.current) return

                      const selectedPlace = autocompleteRef.current.getPlace()

                      if (!selectedPlace?.address_components) return

                      const zipComponent = selectedPlace.address_components.find((c: any) =>
                        c.types.includes('postal_code')
                      )

                      if (!zipComponent) return

                      const zipcode = zipComponent.long_name
                      const address = selectedPlace.formatted_address || ''

                      if (!formData.serviceAreas.some((z) => z.zipcode === zipcode)) {
                        setFormData((prev) => ({
                          ...prev,
                          serviceAreas: [...prev.serviceAreas, { zipcode, address }],
                        }))
                      }

                      setError((prev) => ({
                        ...prev,
                        serviceAreas: '',
                      }))
                    }}
                    options={{
                      types: ['(regions)'],
                    }}
                  >
                    <input
                      type='number'
                      min={0}
                      className={`form-control ${error.serviceAreas ? 'is-invalid' : ''}`}
                      placeholder='Type zipcode and select'
                      autoComplete='off'
                    />
                  </Autocomplete>

                  {error.serviceAreas && (
                    <div className='invalid-feedback d-block'>
                      {error.serviceAreas}
                    </div>
                  )}
                </div>
              )}

              {/* ABN number (Only for allied_health) */}
              {(formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='ABNNumber' className='required form-label fw-bold text-black'>
                    ABN Number
                  </label>
                  <input
                    type='text'
                    id='ABNNumber'
                    className={`form-control ${error.ABNNumber ? 'is-invalid' : ''}`}
                    name='ABNNumber'
                    value={formData.ABNNumber}
                    onChange={handleChange}
                    placeholder='Enter ABN Number'
                    maxLength={12}
                    autoComplete='off'
                    required
                  />
                  {error.ABNNumber && <div className='invalid-feedback'>{error.ABNNumber}</div>}
                </div>
              )}

              {/* Service Provider Number (Only for service_provider) */}
              {(formData.userType === 'service_provider' || formData.userType === 'ndis_business') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='serviceProiderNumber' className='required form-label fw-bold text-black'>
                    Service Provider Number
                  </label>
                  <input
                    type='text'
                    id='serviceProiderNumber'
                    className={`form-control ${error.serviceProiderNumber ? 'is-invalid' : ''}`}
                    name='serviceProiderNumber'
                    value={formData.serviceProiderNumber}
                    onChange={handleChange}
                    placeholder='Enter ABN Number'
                    maxLength={12}
                    autoComplete='off'
                    required
                  />
                  {error.serviceProiderNumber && <div className='invalid-feedback'>{error.serviceProiderNumber}</div>}
                </div>
              )}

              {/* Business name (Only for allied_health) */}
              {(formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='businessName' className='required form-label fw-bold text-black'>Business Name</label>
                  <input
                    type='text'
                    id='businessName'
                    className={`form-control ${error.businessName ? 'is-invalid' : ''}`}
                    name='businessName'
                    value={formData.businessName}
                    onChange={handleChange}
                    placeholder='Enter business name'
                    autoComplete='off'
                    required
                  />
                  {error.businessName && <div className='invalid-feedback'>{error.businessName}</div>}
                </div>
              )}

              {/* Business Sector (Only for allied_health) */}
              {(formData.userType === 'allied_health' || formData.userType === 'service_provider') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='businessSector' className='required form-label fw-bold text-black'>
                    Business Sector
                  </label>

                  <select
                    id='businessSector'
                    name='businessSector'
                    value={formData.businessSector}
                    onChange={handleChange}
                    className={`form-select ${error.businessSector ? 'is-invalid' : ''}`}
                  >
                    <option value=''>Select business sector</option>

                    {Array.isArray(businessSector) &&
                      businessSector.map((sector: any) => (
                        <option key={sector.id} value={sector.id}>
                          {sector.sector_name}
                        </option>
                      ))}
                  </select>

                  {error.businessSector && <div className='invalid-feedback'>{error.businessSector}</div>}
                </div>
              )}

              {/* Business tyoe (Only for ndis_business) */}
              {formData.userType === 'ndis_business' && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='businessType' className='required form-label fw-bold text-black'>
                    Business Type
                  </label>

                  <select
                    id='businessType'
                    name='businessType'
                    value={formData.businessType}
                    onChange={handleChange}
                    className={`form-select ${error.businessType ? 'is-invalid' : ''}`}
                  >
                    <option value=''>Select business type</option>

                    {Array.isArray(businessType) &&
                      businessType.map((type: any) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                  </select>

                  {error.businessType && <div className='invalid-feedback'>{error.businessType}</div>}
                </div>
              )}

              {/* Facebook Link (Only for allied_health) */}
              {(formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='facebookURL' className='form-label fw-bold text-black'>Facebook URL</label>
                  <input
                    type='text'
                    id='facebookURL'
                    className={`form-control ${error.facebookURL ? 'is-invalid' : ''}`}
                    name='facebookURL'
                    value={formData.facebookURL}
                    onChange={handleChange}
                    placeholder='Enter facebook URL'
                    autoComplete='off'
                    required
                  />
                  {error.facebookURL && <div className='invalid-feedback'>{error.facebookURL}</div>}
                </div>
              )}

              {/* Instagram Link (Only for allied_health) */}
              {(formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='instagramURL' className='form-label fw-bold text-black'>Instagram URL</label>
                  <input
                    type='text'
                    id='instagramURL'
                    className={`form-control ${error.instagramURL ? 'is-invalid' : ''}`}
                    name='instagramURL'
                    value={formData.instagramURL}
                    onChange={handleChange}
                    placeholder='Enter instagram URL'
                    autoComplete='off'
                    required
                  />
                  {error.instagramURL && <div className='invalid-feedback'>{error.instagramURL}</div>}
                </div>
              )}

              {/* Linkedin Link (Only for allied_health) */}
              {(formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && (
                <div className='col-md-6 mb-7'>
                  <label htmlFor='linkedinURL' className='form-label fw-bold text-black'>Linkedin URL</label>
                  <input
                    type='text'
                    id='linkedinURL'
                    className={`form-control ${error.linkedinURL ? 'is-invalid' : ''}`}
                    name='linkedinURL'
                    value={formData.linkedinURL}
                    onChange={handleChange}
                    placeholder='Enter linkedin URL'
                    autoComplete='off'
                    required
                  />
                  {error.linkedinURL && <div className='invalid-feedback'>{error.linkedinURL}</div>}
                </div>
              )}

              {/* Provided Service Tag Input */}
              {(formData.userType === 'allied_health' || formData.userType === 'service_provider' || formData.userType === 'ndis_business') && (
                <div className='col-md-6 mb-7 position-relative'>
                  <label className='required form-label fw-bold text-black'>
                    Service You Provided
                  </label>

                  {/* Tag List */}
                  <div className='d-flex flex-wrap gap-2'>
                    {formData.providedService.map((tag, index) => (
                      <span key={index} className='badge bg-success d-flex align-items-center text-white mb-2'>
                        {tag}
                        <button
                          type='button'
                          className='btn-close btn-close-white ms-2'
                          style={{ fontSize: '10px' }}
                          onClick={() => removeProvidedService(index)}
                        />
                      </span>
                    ))}
                  </div>

                  {/* Input */}
                  <input
                    type="text"
                    className={`form-control ${error.providedService ? 'is-invalid' : ''}`}
                    placeholder="Search & select service"
                    value={providedServiceInput}
                    onChange={async (e) => {
                      const value = e.target.value
                      setProvidedServiceInput(value)

                      if (value.length < 2) {
                        setProvidedServiceSuggestions([])
                        return
                      }

                      try {
                        const res = await getServiceSuggestions(value)
                        const suggestions = res?.data || res?.responseObject || res || []
                        setProvidedServiceSuggestions(Array.isArray(suggestions) ? suggestions : [])
                      } catch (err) {
                        console.error("Error fetching service suggestions:", err)
                        setProvidedServiceSuggestions([])
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.preventDefault()
                    }}
                  />

                  {/* Suggestions Dropdown */}
                  {providedServiceSuggestions.length > 0 && (
                    <div className="border rounded bg-white mt-1 position-absolute w-100 shadow-sm"
                      style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}
                    >
                      {providedServiceSuggestions.map((item: any, i: number) => (
                        <div
                          key={i}
                          className="p-2 cursor-pointer hover:bg-gray-50"
                          style={{ cursor: "pointer", color: "black" }}
                          onClick={() => selectProvidedService(item)}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  )}

                  {error.providedService && (
                    <div className='invalid-feedback d-block'>{error.providedService}</div>
                  )}
                </div>
              )}

              {(formData.userType === 'service_provider' || formData.userType === 'ndis_business' || formData.userType === 'support_worker' || formData.userType === 'nurse') && (
                <div className="col-md-6 mb-7">
                  <label className="form-label fw-bold text-black">Location</label>

                  {/* INPUT + ADD BUTTON */}
                  <div className="d-flex align-items-center gap-2 mb-4 w-100">
                    <Autocomplete
                      onLoad={(auto) => (locationAutoRef.current = auto)}
                      options={{ types: ['geocode'] }}
                      className='w-100'
                    >
                      <input
                        id="locationInput"
                        type="text"
                        className="form-control"
                        placeholder="Enter location"
                      />
                    </Autocomplete>

                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleAddLocation}
                    >
                      +
                    </button>
                  </div>

                  {/* LOCATION LIST */}
                  {locations.map((loc, index) => (
                    <div key={index} className="card p-3 mb-3">

                      {/* HEADER */}
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{loc.address}</h6>
                          <small className="text-muted">
                            Lat: {loc.latitude ? loc.latitude.toFixed(4) : '-'},
                            Lng: {loc.longitude ? loc.longitude.toFixed(4) : '-'}
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => removeLocation(index)}
                        />
                      </div>

                      {/* RADIUS */}
                      <div className="mt-3 d-flex align-items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          className="form-control"
                          value={loc.radiusKm}
                          placeholder="Service radius (0-100)"
                          onChange={(e) =>
                            handleRadiusChange(index, e.target.value === '' ? '' : Number(e.target.value))
                          }
                        />
                        <span>km</span>
                      </div>

                    </div>
                  ))}
                </div>
              )}

            </div>
            {/* Actions */}
            <div className='d-flex justify-content-end'>
              <button
                type='button'
                className='btn btn-light me-3'
                onClick={() => navigate('/users')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={loading}
              >
                {loading
                  ? (id ? 'Updating...' : 'Creating...')
                  : (id ? 'Update User' : 'Create User')
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default UserAddPage
